/**
 * Closing call to action.
 *
 * The last screen repeats the primary action for a visitor who scrolled the whole
 * page, plus the short beta note so the invite-only rule is not a surprise at
 * sign-up. The destination is decided by the caller from the session state.
 */

import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { cn } from '../../lib/format'

export interface LandingCtaProps {
  /** Destination of the primary action, decided by the session. */
  primaryTo: string
  /** Label of the primary action. */
  primaryLabel: string
  className?: string
}

export function LandingCta({ primaryTo, primaryLabel, className }: LandingCtaProps) {
  const { t } = useTranslation()

  return (
    <section className={cn('landing-section', className)} aria-labelledby="landing-cta-title">
      <div className="landing-container">
        <div className="border-border bg-card flex flex-col items-start gap-2.5 rounded-3xl border p-3.5 sm:gap-4 sm:p-10">
          <h2
            id="landing-cta-title"
            className="max-w-2xl text-2xl font-semibold tracking-tight text-balance sm:text-3xl"
          >
            {t('home.finalCtaTitle')}
          </h2>
          <p className="text-muted-foreground max-w-2xl text-sm sm:text-base">
            {t('home.finalCtaBody')}
          </p>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Link className="landing-cta-solid" to={primaryTo}>
              {primaryLabel}
              <ArrowRight className="size-4" aria-hidden />
            </Link>
            <p className="text-muted-foreground text-xs">{t('home.betaMeta')}</p>
          </div>
        </div>
      </div>
    </section>
  )
}
